import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CopyrightIcon from '@mui/icons-material/Copyright';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import GroupIcon from '@mui/icons-material/Group';
import PaymentsIcon from '@mui/icons-material/Payments';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import SettingsIcon from '@mui/icons-material/Settings';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';
import SpeedIcon from '@mui/icons-material/Speed';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import { Collapse } from '@mui/material';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import * as React from 'react';
import { useState, type ReactElement } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { hasArrived, type PendingNavigation } from './pendingNavigation';
import { useAppContext } from '../../AppContext';
import { BrandType } from '../../models/enums/BrandType';
import { useCurrentBrand } from '../../hooks/useCurrentBrand';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';

export interface DrawerItemsProps {
    // `path` is the destination the clicked item links to. Frame holds the title until the route actually
    // reaches it, so a navigation the user cancels never renames the page (TTD-151).
    onItemClick: (event: React.MouseEvent<HTMLElement>, key: string, text: string, path: string) => void;
}

// The selected/hover indicator every sub-menu item in this drawer carries. Extracted here rather than
// repeated inline a third time — the existing groups still inline their own copy, so if you restyle one,
// this is the other place to look.
const promotionSubItemSx = {
    "&.Mui-selected .MuiListItemText-primary": {
        fontWeight: "bold",
    },
    "&.Mui-selected::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "4px",
        backgroundColor: "primary.main",
    },
    "&.Mui-selected:hover::before": {
        backgroundColor: "primary.main",
        width: "4px",
    },
    "&:hover::before": {
        content: '""',
        position: "absolute",
        left: 0,
        top: 0,
        bottom: 0,
        width: "2px",
        backgroundColor: "primary.main",
    },
    pl: 10,
} as const;

const DrawerItems = ({ onItemClick }: DrawerItemsProps): ReactElement => {
    const [selectedMenu, setSelectedMenu] = React.useState("");
    const [selectedDrawerListItemKey, setSelectedDrawerListItemKey] = useState<string | undefined>();
    const routerLocation = useLocation();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const { data: currentBrand } = useCurrentBrand();
    const feedbackEnabled = currentBrand?.touchTabFeedbackEnabled ?? false;
    // Tips only apply to restaurant brands.
    const isRestaurantBrand = currentBrand?.type === BrandType.Restaurant;
    const { canRead } = usePermissions();

    // When a super-admin is viewing the TouchTab org itself, the menu is restricted to TouchTab-only surfaces:
    // Admin → Configuration, Admin → Oracle Cert, and Users. Everything else (a customer-org's location,
    // payments, feedback, devices, config) is hidden.
    const touchtabScope = app.isViewingSuperAdminOrg();

    // Per-item read access → drives both item and group visibility. A group is shown only if at least
    // one of its items is visible. (The Admin group is super-admin-only and gated separately below.)
    // The Location menu (employees/health/pairing code) acts on the selected location — only show it when one
    // is actually selected in the top scope picker.
    const showLocation = !touchtabScope && !!app.locationId && canRead(Perm.location);
    const showRestaurant = true;
    const showPayments = !touchtabScope && canRead(Perm.paymentsTransactions);
    const showFeedback = !touchtabScope && feedbackEnabled && canRead(Perm.feedback);
    const showDevices = !touchtabScope && canRead(Perm.devices);
    // Knox device health is brand-scoped; show once a brand is selected. (Provider-gating on the brand having a
    // Knox config — like loyalty — is a follow-up.)
    const showKnoxDeviceHealth = !touchtabScope && !!app.brandId && canRead(Perm.devices);
    const showTips = !touchtabScope && isRestaurantBrand && canRead(Perm.configurationTips);
    // Revenue centers and channels come from the POS, so this only means anything for a restaurant brand,
    // and it acts on the selected location — same shape as the Location group above.
    const showRevenueCenters = !touchtabScope && isRestaurantBrand && !!app.locationId && canRead(Perm.configurationRevenueCenters);
    const showOnboarding = !touchtabScope && canRead(Perm.configurationOnboarding);
    // Promotions are brand-scoped (media, schedules and day parts all hang off a brand), so the item only
    // means anything once a brand is selected in the top scope picker.
    //
    // This is a TOP-LEVEL item rather than a child of the Content group, because that whole group — Menus,
    // Promotions, Upsells — is commented out below and its other two pages are still empty placeholders.
    // Re-enabling the group to reach promotions would expose those too. Fold this back into Content when
    // they're built.
    // Each sub-item is separately grantable, so each is checked on its own and the group shows if any of
    // them does. A user with only Reports sees Media Network > Reports and nothing else under it.
    const mediaNetworkScope = !touchtabScope && !!app.brandId;
    const showAdBanner = mediaNetworkScope && canRead(Perm.mediaNetworkAdBanner);
    const showCrossSell = mediaNetworkScope && canRead(Perm.mediaNetworkCrossSell);
    // The library, advertisers and tags are shared across features rather than owned by one, which is why
    // they sit beside Ad Banner and Cross-Sell instead of inside them.
    const showMediaLibrary = mediaNetworkScope && canRead(Perm.mediaNetworkMediaLibrary);
    const showAdvertisers = mediaNetworkScope && canRead(Perm.mediaNetworkAdvertisers);
    const showMediaTags = mediaNetworkScope && canRead(Perm.mediaNetworkTags);
    const showMediaReports = mediaNetworkScope && canRead(Perm.mediaNetworkReports);
    const showMediaNetwork = showAdBanner || showCrossSell || showMediaLibrary
        || showAdvertisers || showMediaTags || showMediaReports;
    // Day parts are brand-level, so like promotions they only mean anything once a brand is selected. They
    // carry their OWN permission rather than borrowing the promotions one: they're an AccountManagement
    // concept built for cross-product reuse, so the next consumer inherits the right gate instead of a
    // promotions-shaped one.
    const showDayParts = !touchtabScope && !!app.brandId && canRead(Perm.configurationDayParts);
    const showContentStrings = !touchtabScope && !!app.brandId && canRead(Perm.configurationContentStrings);
    const showConfiguration = showTips || showDayParts || showRevenueCenters || showOnboarding || showContentStrings;
    const showUsers = canRead(Perm.users);

    // Admin. Every item needs TouchTab-org membership AND a grant — membership alone used to be the whole
    // gate on most of this section, which made being in the org the permission (TTD-159).
    //
    // Three grants cover the section: adminConfiguration for the reference-data screens, adminSkinning for
    // Skinning, and adminDeletedLocations for restoring or destroying a location, which is a different job
    // from the rest of it.
    const adminScope = app.isSuperAdmin();
    const showAdminConfiguration = adminScope && canRead(Perm.adminConfiguration);
    const showAdminSkinning = adminScope && canRead(Perm.adminSkinning);
    const showDeletedLocations = adminScope && canRead(Perm.adminDeletedLocations);
    // The group header follows its contents. Without this a super admin holding none of the three saw an
    // "Admin" heading that expanded to nothing.
    const showAdmin = showAdminConfiguration || showAdminSkinning || showDeletedLocations;

    // The highlight moves only once the route actually reaches the clicked item, so answering "Stay" on the
    // unsaved-changes prompt leaves it where it was (TTD-151).
    //
    // Held against the DESTINATION PATH rather than a key derived from the URL. Deriving one cannot work here:
    // the item keys are not a function of the route — "dashboard" and "analytics" both point at /users/users,
    // and "devices-status" lives at /knox/device-health. An earlier attempt derived the key from
    // pathname.split("/")[1], which on every route in this app is the ORG SLUG, so it matched no item and
    // nothing highlighted at all.
    const pendingSelection = React.useRef<PendingNavigation<string> | null>(null);

    React.useEffect(() => {
        const pending = pendingSelection.current;
        if (!pending) return;

        // Reached only when the route has changed. If it went somewhere other than the clicked item, the
        // intent is stale and is dropped rather than applied to a page the user never asked for.
        if (hasArrived(pending, routerLocation.pathname)) setSelectedDrawerListItemKey(pending.value);
        pendingSelection.current = null;
    }, [routerLocation.pathname]);

    const handleDrawerListItemClick = (event: React.MouseEvent<HTMLElement>, key: string, menu: string, subMenu: string) => {
        // Read from the rendered link, so the destination cannot drift out of step with the `to` prop beside
        // it. Read synchronously — currentTarget is only valid for the duration of the handler.
        const path = (event.currentTarget as HTMLAnchorElement).pathname;
        pendingSelection.current = { path, value: key };

        const titleText = subMenu == "" ? menu : selectedMenu + " / " + subMenu;
        onItemClick(event, key, titleText, path);
    };

    const handleMenuClick = (menu: string) => {
        if (selectedMenu === menu) {
            setSelectedMenu("");
        }
        else {
            setSelectedMenu(menu)
        }
    };

    return (
        <Box
            sx={{
                overflow: 'auto',
                '& .MuiListItemButton-root.Mui-selected': {
                    color: 'primary.main',
                    '& .MuiListItemIcon-root': {
                        color: 'primary.main',
                    },
                },
            }}
        >
            <List>
                {showAdmin &&
                    <ListItem key="admin" disablePadding>
                        <ListItemButton
                            onClick={() => handleMenuClick(strings.drawerAdmin)}
                        >
                            <ListItemIcon>
                                <AdminPanelSettingsIcon />
                            </ListItemIcon>
                            <ListItemText primary={strings.drawerAdmin} />
                            {selectedMenu == strings.drawerAdmin ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                    </ListItem>
                }

                {showAdmin &&
                    <Collapse in={selectedMenu == strings.drawerAdmin} timeout="auto" unmountOnExit>
                        <List dense component="div" disablePadding>
                            {showAdminConfiguration &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/configuration/organizations`}
                                selected={selectedDrawerListItemKey === "admin-configuration"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-configuration", "", strings.configuration)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.configuration} />
                            </ListItemButton>
                            }
                            {!touchtabScope && (<>
                            {showAdminSkinning &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/skinning`}
                                selected={selectedDrawerListItemKey === "admin-skinning"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-skinning", "", strings.skinning)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.skinning} />
                            </ListItemButton>
                            }
                            {showAdminConfiguration &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/content-strings`}
                                selected={selectedDrawerListItemKey === "admin-content-strings"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-content-strings", "", strings.contentStringCatalogue)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.contentStringCatalogue} />
                            </ListItemButton>
                            }
                            {showAdminConfiguration &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/advertisers`}
                                selected={selectedDrawerListItemKey === "admin-advertisers"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-advertisers", "", strings.advertisers)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.advertisers} />
                            </ListItemButton>
                            }
                            {showDeletedLocations && (
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/deleted-locations`}
                                selected={selectedDrawerListItemKey === "admin-deleted-locations"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-deleted-locations", "", strings.deletedLocations)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.deletedLocations} />
                            </ListItemButton>
                            )}
                            </>)}
                            {showAdminConfiguration &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/admin/oracle-certification`}
                                selected={selectedDrawerListItemKey === "admin-oracle-certification"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "admin-oracle-certification", "", "Oracle Cert")}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary="Oracle Cert" />
                            </ListItemButton>
                            }
                        </List>
                    </Collapse>
                }

                {showLocation && (<>
                <ListItem key="location" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerLocation)}
                    >
                        <ListItemIcon>
                            <StorefrontIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerLocation} />
                        {selectedMenu == strings.drawerLocation ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerLocation} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        {/* Employees + Health nav temporarily hidden.
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/location/employees`}
                            selected={selectedDrawerListItemKey === "location-employees"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "location-employees", "", strings.employees)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.employees} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/location/health`}
                            selected={selectedDrawerListItemKey === "location-health"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "location-health", "",strings.health)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.health} />
                        </ListItemButton>
                        */}
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/location/pairing-code`}
                            selected={selectedDrawerListItemKey === "location-pairing-code"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "location-pairing-code", "",strings.pairingCode)}
                           sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.pairingCode} />
                        </ListItemButton>
                    </List>
                </Collapse>
                </>)}

                {showRestaurant && (<>
                <ListItem key="restaurant" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick("Restaurant")}
                    >
                        <ListItemIcon>
                            <RestaurantIcon />
                        </ListItemIcon>
                        <ListItemText primary="Restaurant" />
                        {selectedMenu == "Restaurant" ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == "Restaurant"} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/restaurant/items`}
                            selected={selectedDrawerListItemKey === "restaurant-items"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "restaurant-items", "", "Menu Items")}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary="Menu Items" />
                        </ListItemButton>
                    </List>
                </Collapse>
                </>)}
{/*
                <ListItem key="dashboard" disablePadding>
                    <ListItemButton
                        component={Link} to={`/${app.organizationSlug}/users/users`}
                        selected={selectedDrawerListItemKey === "dashboard"}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            handleMenuClick(strings.drawerDashboard);  
                            handleDrawerListItemClick(event, "dashboard", strings.drawerDashboard, "");
                        }}
                    >
                        <ListItemIcon>
                            <SpeedIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerDashboard} />
                    </ListItemButton>
                </ListItem>

                <ListItem key="content" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerContent)}
                    >
                        <ListItemIcon>
                            <CopyrightIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerContent} />
                        {selectedMenu == strings.drawerContent ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerContent} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/content/menus`}
                            selected={selectedDrawerListItemKey === "content-menus"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "content-menus", "", strings.menus)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.menus} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/content/promotions`}
                            selected={selectedDrawerListItemKey === "content-promotions"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "content-promotions", "", strings.promotions)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.promotions} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/content/upsells`}
                            selected={selectedDrawerListItemKey === "content-upsells"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "content-upsells", "", strings.upsells)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.upsells} />
                        </ListItemButton>
                    </List>
                </Collapse>
*/}
                {showMediaNetwork &&
                    <ListItem key="media-network" disablePadding>
                        <ListItemButton
                            onClick={() => handleMenuClick(strings.mediaNetwork)}
                        >
                            <ListItemIcon>
                                <SlideshowIcon />
                            </ListItemIcon>
                            <ListItemText primary={strings.mediaNetwork} />
                            {selectedMenu == strings.mediaNetwork ? <ExpandLess /> : <ExpandMore />}
                        </ListItemButton>
                    </ListItem>
                }

                {showMediaNetwork &&
                    <Collapse in={selectedMenu == strings.mediaNetwork} timeout="auto" unmountOnExit>
                        <List dense component="div" disablePadding>
                            {/* "Ad Banner" is this nav item's name only — the pages below it still call the
                                thing a playlist, which is what it is: an ordered set of media that loops. */}
                            {showAdBanner &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network`}
                                selected={selectedDrawerListItemKey === "media-network-ad-banner"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-ad-banner", "", strings.adBanner)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.adBanner} />
                            </ListItemButton>
                            }
                            {showCrossSell &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network/cross-sell`}
                                selected={selectedDrawerListItemKey === "media-network-cross-sell"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-cross-sell", "", strings.crossSell)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.crossSell} />
                            </ListItemButton>
                            }
                            {/* The library, advertisers and tags sit BESIDE the features, not inside one:
                                Cross-Sell draws on the same creatives, advertisers and tags. */}
                            {showMediaLibrary &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network/media-library`}
                                selected={selectedDrawerListItemKey === "media-network-media-library"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-media-library", "", strings.mediaLibrary)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.mediaLibrary} />
                            </ListItemButton>
                            }
                            {showAdvertisers &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network/advertisers`}
                                selected={selectedDrawerListItemKey === "media-network-advertisers"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-advertisers", "", strings.playlistAdvertisers)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.playlistAdvertisers} />
                            </ListItemButton>
                            }
                            {showMediaTags &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network/tags`}
                                selected={selectedDrawerListItemKey === "media-network-tags"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-tags", "", strings.playlistTagsTitle)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.playlistTagsTitle} />
                            </ListItemButton>
                            }
                            {showMediaReports &&
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/content/media-network/reports`}
                                selected={selectedDrawerListItemKey === "media-network-reports"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "media-network-reports", "", strings.reports)}
                                sx={promotionSubItemSx}
                            >
                                <ListItemText primary={strings.reports} />
                            </ListItemButton>
                            }
                        </List>
                    </Collapse>
                }

                {showPayments && (<>
                <ListItem key="payments" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerPayments)}
                    >
                        <ListItemIcon>
                            <PaymentsIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerPayments} />
                        {selectedMenu == strings.drawerPayments ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerPayments} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/payments/transactions`}
                            selected={selectedDrawerListItemKey === "payments-transactions"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "payments-transactions", "", strings.transactions)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.transactions} />
                        </ListItemButton>
                        {/* Reports nav temporarily hidden.
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/payments/reports`}
                            selected={selectedDrawerListItemKey === "payments-reports"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "payments-reports", "", strings.reports)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.reports} />
                        </ListItemButton>
                        */}
                    </List>
                </Collapse>
                </>)}

                {showFeedback && (<>
                <ListItem key="feedback" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerFeedback)}
                    >
                        <ListItemIcon>
                            <ThumbUpIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerFeedback} />
                        {selectedMenu == strings.drawerFeedback ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerFeedback} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/feedback/campaigns`}
                            selected={selectedDrawerListItemKey === "feedback-campaigns"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "feedback-campaigns", "", strings.campaigns)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.campaigns} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/feedback/configuration`}
                            selected={selectedDrawerListItemKey === "feedback-configuration"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "feedback-configuration", "", strings.configuration)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.configuration} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/feedback/reports`}
                            selected={selectedDrawerListItemKey === "feedback-reports"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "feedback-reports", "", strings.reports)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.reports} />
                        </ListItemButton>
                    </List>
                </Collapse>
                </>)}
{/*
                <ListItem key="support" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerSupport)}
                    >
                        <ListItemIcon>
                            <SupportAgentIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerSupport} />
                         {selectedMenu == strings.drawerSupport ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerSupport} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/support/service-tickets`}
                            selected={selectedDrawerListItemKey === "support-service-tickets"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "support-service-tickets", "", strings.serviceTickets)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.serviceTickets} />
                        </ListItemButton>
                        <ListItemButton
                            component={Link}
                             to={`/${app.organizationSlug}/support/knowledge-base`}
                            selected={selectedDrawerListItemKey === "support-knowledge-base"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "support-knowledge-base", "", strings.knowledgeBase)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.knowledgeBase} />
                        </ListItemButton>
                    </List>
                </Collapse>

                <ListItem key="analytics" disablePadding>
                    <ListItemButton
                        component={Link} to={`/${app.organizationSlug}/users/users`}
                        selected={selectedDrawerListItemKey === "analytics"}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            handleMenuClick(strings.drawerAnalytics);  
                            handleDrawerListItemClick(event, "analytics", strings.drawerAnalytics, "");
                        }}
                    >
                        <ListItemIcon>
                            <AssessmentIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerAnalytics} />
                    </ListItemButton>
                </ListItem>
*/}
                {showDevices && (<>
                <ListItem key="devices" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerDevices)}
                    >
                        <ListItemIcon>
                            <PhoneAndroidIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerDevices} />
                        {selectedMenu == strings.drawerDevices ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerDevices} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/devices`}
                            selected={selectedDrawerListItemKey === "devices-information"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "devices-information", "", strings.information)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": { fontWeight: "bold" },
                                "&.Mui-selected::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "primary.main" },
                                "&.Mui-selected:hover::before": { backgroundColor: "primary.main", width: "4px" },
                                "&:hover::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: "2px", backgroundColor: "primary.main" },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.information} />
                        </ListItemButton>
                        {showKnoxDeviceHealth && (
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/knox/device-health`}
                            selected={selectedDrawerListItemKey === "devices-status"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "devices-status", "", strings.status)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": { fontWeight: "bold" },
                                "&.Mui-selected::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: "4px", backgroundColor: "primary.main" },
                                "&.Mui-selected:hover::before": { backgroundColor: "primary.main", width: "4px" },
                                "&:hover::before": { content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: "2px", backgroundColor: "primary.main" },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.status} />
                        </ListItemButton>
                        )}
                    </List>
                </Collapse>
                </>)}

                {showConfiguration && (<>
                <ListItem key="configuration" disablePadding>
                    <ListItemButton
                        onClick={() => handleMenuClick(strings.drawerConfiguration)}
                    >
                        <ListItemIcon>
                            <SettingsIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerConfiguration} />
                        {selectedMenu == strings.drawerConfiguration ? <ExpandLess /> : <ExpandMore />}
                    </ListItemButton>
                </ListItem>

                <Collapse in={selectedMenu == strings.drawerConfiguration} timeout="auto" unmountOnExit>
                    <List dense component="div" disablePadding>
                        {/* Location Groups nav TEMPORARILY DISABLED while the CheckPlease tempdb is the source of
                            truth for tips/feedback schedules (keeping location-group changes in sync with tempdb is
                            too fragile). Re-enable this whole block once AccountManagement.TipConfiguration/Feedback
                            is the single source of truth and tempdb is gone.
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/configuration/location-groups`}
                            selected={selectedDrawerListItemKey === "configuration-location-groups"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-location-groups", "", strings.locationGroups)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.locationGroups} />
                        </ListItemButton>
                        */}
                        {showContentStrings && (
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/configuration/content-strings`}
                                selected={selectedDrawerListItemKey === "configuration-content-strings"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-content-strings", "", strings.contentStrings)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.contentStrings} />
                            </ListItemButton>
                        )}
                        {showTips && (
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/configuration/tips`}
                                selected={selectedDrawerListItemKey === "configuration-tips"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-tips", "", strings.tips)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.tips} />
                            </ListItemButton>
                        )}
                        {showDayParts && (
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/configuration/day-parts`}
                                selected={selectedDrawerListItemKey === "configuration-day-parts"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-day-parts", "", strings.dayParts)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.dayParts} />
                            </ListItemButton>
                        )}
                        {showRevenueCenters && (
                            <ListItemButton
                                component={Link}
                                to={`/${app.organizationSlug}/configuration/revenue-centers`}
                                selected={selectedDrawerListItemKey === "configuration-revenue-centers"}
                                onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-revenue-centers", "", strings.revenueCenters)}
                                sx={{
                                    "&.Mui-selected .MuiListItemText-primary": {
                                        fontWeight: "bold",
                                    },
                                    "&.Mui-selected::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "4px",
                                        backgroundColor: "primary.main",
                                    },
                                    "&.Mui-selected:hover::before": {
                                        backgroundColor: "primary.main",
                                        width: "4px",
                                    },
                                    "&:hover::before": {
                                        content: '""',
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        bottom: 0,
                                        width: "2px",
                                        backgroundColor: "primary.main",
                                    },
                                    pl: 10
                                }}
                            >
                                <ListItemText primary={strings.revenueCenters} />
                            </ListItemButton>
                        )}
                        {showOnboarding && (
                        <ListItemButton
                            component={Link}
                            to={`/${app.organizationSlug}/configuration/onboarding`}
                            selected={selectedDrawerListItemKey === "configuration-onboarding"}
                            onClick={(event: React.MouseEvent<HTMLElement>) => handleDrawerListItemClick(event, "configuration-onboarding", "", strings.onboarding)}
                            sx={{
                                "&.Mui-selected .MuiListItemText-primary": {
                                    fontWeight: "bold",
                                },
                                "&.Mui-selected::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "4px",
                                    backgroundColor: "primary.main",
                                },
                                "&.Mui-selected:hover::before": {
                                    backgroundColor: "primary.main",
                                    width: "4px",
                                },
                                "&:hover::before": {
                                    content: '""',
                                    position: "absolute",
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: "2px",
                                    backgroundColor: "primary.main",
                                },
                                pl: 10
                            }}
                        >
                            <ListItemText primary={strings.onboarding} />
                        </ListItemButton>
                        )}
                    </List>
                </Collapse>
                </>)}

                {showUsers && (
                <ListItem key="users" disablePadding>
                    <ListItemButton
                        component={Link} to={`/${app.organizationSlug}/users/users`}
                        selected={selectedDrawerListItemKey === "users"}
                        onClick={(event: React.MouseEvent<HTMLElement>) => {
                            handleMenuClick(strings.drawerUsers);
                            handleDrawerListItemClick(event, "users", strings.drawerUsers, "");
                        }}
                    >
                        <ListItemIcon>
                            <GroupIcon />
                        </ListItemIcon>
                        <ListItemText primary={strings.drawerUsers} />
                    </ListItemButton>
                </ListItem>
                )}

            </List>
        </Box>
    );
}

export default DrawerItems;
